import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const BatchCodeVerifyModal = ({ show, onHide, items, onVerified }) => {
  const [verifiedIds, setVerifiedIds] = useState(new Set());
  const [scanValue, setScanValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setVerifiedIds(new Set());
      setScanValue('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show]);

  const batchCodeMap = React.useMemo(() => {
    const map = new Map();
    items.forEach(item => {
      const code = item.product_batch?.batch_code;
      if (code) {
        const ids = map.get(code) || [];
        ids.push(item.id);
        map.set(code, ids);
      }
    });
    return map;
  }, [items]);

  const allVerified = verifiedIds.size === items.length;

  const handleScan = e => {
    e.preventDefault();
    const code = scanValue.trim();
    if (!code) return;

    const candidateIds = batchCodeMap.get(code);

    if (!candidateIds?.length) {
      toast.error('Invalid batch code. Please scan a valid code.');
      setScanValue('');
      return;
    }

    const nextItemId = candidateIds.find(id => !verifiedIds.has(id));

    if (!nextItemId) {
      toast.info('This batch code has already been verified.');
      setScanValue('');
      return;
    }

    setVerifiedIds(prev => new Set([...prev, nextItemId]));
    setScanValue('');
  };

  const handleClose = () => {
    setVerifiedIds(new Set());
    setScanValue('');
    onHide();
  };

  const handleConfirm = () => {
    onVerified();
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Batch Code Verification</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleScan} className="mb-3">
          <Form.Group className="d-flex gap-2">
            <Form.Control
              ref={inputRef}
              type="text"
              value={scanValue}
              onChange={e => setScanValue(e.target.value)}
              placeholder="Scan batch code..."
              autoFocus
            />
            <Button type="submit" variant="primary" style={{ minWidth: 80 }}>
              Verify
            </Button>
          </Form.Group>
          <Form.Text className="text-muted">
            Scan each item's batch code to verify before submitting.
          </Form.Text>
        </Form>

        <div className="mb-2 text-end">
          <span className="fw-bold">
            {verifiedIds.size} / {items.length}
          </span>{' '}
          verified
        </div>

        <Table striped bordered size="sm">
          <thead>
            <tr className="bg-light">
              <th style={{ width: 50 }} className="text-center">
                <FontAwesomeIcon icon="check" />
              </th>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Shelf</th>
              <th>Expiry Date</th>
              <th className="text-end">Qty</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const isVerified = verifiedIds.has(item.id);
              return (
                <tr key={item.id} className={isVerified ? 'table-success' : ''}>
                  <td className="text-center">
                    {isVerified && (
                      <FontAwesomeIcon
                        icon="check-circle"
                        className="text-success"
                      />
                    )}
                  </td>
                  <td>{item.product_batch?.product?.sku}</td>
                  <td>{item.product_batch?.product?.name}</td>
                  <td>{item.product_batch?.shelf?.code}</td>
                  <td>{item.product_batch?.expire_date || '-'}</td>
                  <td className="text-end">{item.quantity}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={!allVerified}
        >
          Confirm & Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BatchCodeVerifyModal;
